import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Sparkles, History as HistoryIcon, ArrowUp, ArrowDown, AlertTriangle, Flag } from "lucide-react";

// Spec section 73's correction/training workflow — flagging here NEVER
// changes production AI rules by itself; it only creates a "Suggested
// Training Example" for an admin to review under Administration -> AI
// Intelligence -> Corrections, who must then explicitly apply it to a
// draft and publish that draft for it to ever take effect.
const CorrectionFlag = ({ snapshotId, fieldPath, aiValue }) => {
  const { hasCap } = useAuth();
  const [open, setOpen] = useState(false);
  const [corrected, setCorrected] = useState("");
  const [explanation, setExplanation] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!hasCap("ai.customer.correct")) return null;
  if (done) return <span className="text-[10px] text-muted-foreground shrink-0">Flagged</span>;

  const submit = async () => {
    if (!corrected.trim() || !explanation.trim()) { toast.error("Corrected value and explanation are both required"); return; }
    setBusy(true);
    try {
      await api.post("/ai/admin/corrections", {
        snapshot_id: snapshotId, field_path: fieldPath, ai_value: aiValue,
        corrected_value: corrected.trim(), explanation: explanation.trim(),
      });
      toast.success("Flagged for review — this does not change production AI rules");
      setDone(true); setOpen(false);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[9px] shrink-0" onClick={() => setOpen(true)} data-testid={`ai-flag-${fieldPath}`}>
        <Flag className="h-2.5 w-2.5 mr-1" />Flag incorrect
      </Button>
    );
  }
  return (
    <div className="rounded border p-2 space-y-1 shrink-0 w-56 max-w-full bg-card" data-testid={`ai-flag-form-${fieldPath}`}>
      <Input placeholder="Correct classification" className="h-6 text-[10px]" value={corrected} onChange={(e) => setCorrected(e.target.value)} />
      <Input placeholder="Why? (e.g. spouse is joint decision-maker)" className="h-6 text-[10px]" value={explanation} onChange={(e) => setExplanation(e.target.value)} />
      <div className="flex gap-1">
        <Button size="sm" className="h-6 text-[10px]" disabled={busy} onClick={submit} data-testid={`ai-flag-submit-${fieldPath}`}>Submit</Button>
        <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
};

// Temperature -> badge tone. Purely a display choice — the label itself
// always comes from the backend's own derive_temperature (spec section
// 23: never independently generated, purely derived from Closing
// Probability), never invented client-side.
const TEMP_TONE = {
  Hot: "bg-red-500/15 text-red-600 dark:text-red-400",
  Warm: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  Lukewarm: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  Cold: "bg-slate-500/15 text-slate-600 dark:text-slate-400",
  Converted: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  Lost: "bg-slate-500/15 text-muted-foreground",
  Unknown: "bg-slate-500/10 text-muted-foreground",
};

const CONFIDENCE_LABEL = { high: "High confidence", medium: "Medium confidence", low: "Low confidence" };

const OBJECTION_TONE = {
  blocking: "border-red-500/40 bg-red-500/5",
  high: "border-amber-500/40 bg-amber-500/5",
  medium: "border-border", low: "border-border",
};

const fmtPct = (v) => (v == null ? "—" : `${Math.round(v)}%`);

const ClosingProbabilityDisplay = ({ closing, delta }) => {
  if (closing.state === "converted") return <Badge className={TEMP_TONE.Converted}>Converted / Customer</Badge>;
  if (closing.state === "lost") return <Badge className={TEMP_TONE.Lost}>Lost</Badge>;
  if (closing.state === "unknown" || closing.value == null)
    return <span className="text-sm text-muted-foreground">Cannot determine yet</span>;
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-lg font-semibold tabular-nums" data-testid="ai-closing-probability-value">{fmtPct(closing.value)}</span>
      {delta != null && Math.abs(delta) >= 0.5 && (
        <span className={`inline-flex items-center text-xs font-medium ${delta > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
          {delta > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          {Math.abs(Math.round(delta))}%
        </span>
      )}
      {closing.capped_by && (
        <span className="text-[10px] text-muted-foreground" title={`Capped by: ${closing.capped_by.replaceAll("_", " ")}`}>
          (capped)
        </span>
      )}
    </div>
  );
};

const HistoryDrawer = ({ open, setOpen, history }) => (
  <Sheet open={open} onOpenChange={setOpen}>
    <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="ai-history-drawer">
      <SheetHeader>
        <SheetTitle className="font-heading flex items-center gap-2"><HistoryIcon className="h-4 w-4" />AI Intelligence History</SheetTitle>
      </SheetHeader>
      <div className="mt-4 space-y-3">
        {history.length === 0 && <p className="text-sm text-muted-foreground">No prior snapshots yet.</p>}
        {history.map((h) => (
          <div key={h.snapshot_id} className="rounded-md border p-3 text-xs space-y-1" data-testid={`ai-history-row-${h.snapshot_id}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">{fmtIST(h.generated_at)}</span>
              <Badge variant="outline" className="text-[9px]">{h.trigger_event}</Badge>
            </div>
            {h.status === "failed" ? (
              <p className="text-red-600 dark:text-red-400">Generation failed: {h.error}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-2 tabular-nums" data-testid="ai-history-figures">
                <span>Lead: {h.lead_score?.value != null ? Math.round(h.lead_score.value) : "—"}</span>
                <span>Closing: {h.closing_probability?.state === "active" ? fmtPct(h.closing_probability.value) : (h.closing_probability?.state || "—")}</span>
                <span>{h.temperature || "—"}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);

// Opportunity-scoped (2026-08-30 — was contact-scoped only, which silently
// showed/regenerated whichever opportunity the backend auto-picked,
// regardless of which one was selected on Customer 360). `opportunityId`
// is now required for every API call; `productName` labels which
// product/opportunity this specific assessment is for, so it's never
// ambiguous which analysis is on screen. The panel refetches whenever
// opportunityId changes (switching the selected opportunity on Customer
// 360 switches the AI panel too).
export const CustomerAiIntelligencePanel = ({ contactId, opportunityId, productName, onLegacyFallback }) => {
  const { hasCap } = useAuth();
  const [snapshot, setSnapshot] = useState(undefined); // undefined = loading, null = none yet
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!opportunityId) { setSnapshot(null); return; }
    try {
      const [s, h] = await Promise.all([
        api.get(`/ai/customer/${contactId}/${opportunityId}/snapshot`),
        api.get(`/ai/customer/${contactId}/${opportunityId}/history`),
      ]);
      setSnapshot(s.data || null);
      setHistory(h.data || []);
    } catch {
      setSnapshot(null);
    }
  };

  useEffect(() => { setSnapshot(undefined); if (contactId && opportunityId) load(); }, [contactId, opportunityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (!opportunityId) return;
    setBusy(true);
    try {
      // AI Runtime & Automatic Refresh (2026-09) — the ONE canonical
      // refresh endpoint (V2 always completes before Mentor even starts,
      // so a fresh Mentor briefing can never be generated against a
      // missing/stale V2 snapshot merely because of click order).
      await api.post(`/ai/opportunity/${contactId}/${opportunityId}/refresh`);
      toast.success(`AI Intelligence refreshed${productName ? ` for ${productName}` : ""}`);
      await load();
    } catch (e) {
      toast.error(errDetail(e, "AI unavailable — CRM continues normally"));
    } finally { setBusy(false); }
  };

  if (!opportunityId) return null; // no opportunity selected yet — nothing to analyse
  if (snapshot === undefined) return null; // still loading — avoid a flash of the legacy panel
  if (snapshot === null) {
    // No V2 snapshot exists yet for this contact — fall back to whatever
    // the caller wants to show instead (the legacy prose panel), per
    // spec section 81's "preserve legacy, no forced migration". Still
    // offer a manual Generate action here so a contact can move to V2
    // without waiting for a qualifying event.
    if (!hasCap("ai.customer.view")) return null;
    return (
      <Card data-testid="ai-intelligence-panel-empty">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[hsl(var(--info))]" /> AI Intelligence
            {productName && <Badge variant="outline" className="text-[10px]" data-testid="ai-intelligence-product-badge">{productName}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">No structured AI Intelligence yet for this opportunity.</p>
          {onLegacyFallback}
          {hasCap("ai.customer.generate") && (
            <Button data-testid="ai-intelligence-generate" size="sm" variant="outline" disabled={busy} onClick={generate}>
              {busy ? "Generating…" : "Generate AI Intelligence"}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const prior = history.find((h) => h.snapshot_id !== snapshot.snapshot_id && h.status === "ok");
  const closingDelta = prior && snapshot.closing_probability?.state === "active" && prior.closing_probability?.state === "active"
    ? snapshot.closing_probability.value - prior.closing_probability.value : null;
  const leadScore = snapshot.lead_score || {};
  const closing = snapshot.closing_probability || { state: "unknown", value: null };
  const narrative = snapshot.narrative || {};
  const genuineObjections = (snapshot.objections || []).filter((o) => o.nature === "genuine");
  const softObjections = (snapshot.objections || []).filter((o) => o.nature === "soft_deflective");

  return (
    <Card data-testid="ai-intelligence-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[hsl(var(--info))]" /> AI Intelligence
          {productName && <Badge variant="outline" className="text-[10px]" data-testid="ai-intelligence-product-badge">{productName}</Badge>}
          <Badge variant="outline" className="text-[10px]">Advisory only</Badge>
          {snapshot.config_incomplete && (
            <Badge className="text-[9px] bg-amber-500/15 text-amber-600 dark:text-amber-400" title="This product's AI profile isn't fully configured yet — results use generic/partial guidance.">
              <AlertTriangle className="h-3 w-3 mr-1" />Config incomplete
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ai-score-grid">
          <div className="min-w-0">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Lead Score</p>
            {leadScore.value != null ? (
              <p className="text-lg font-semibold tabular-nums" data-testid="ai-lead-score-value">
                {Math.round(leadScore.value)}<span className="text-xs text-muted-foreground font-normal">/100 — {leadScore.label}</span>
              </p>
            ) : <p className="text-sm text-muted-foreground">Cannot determine</p>}
            {leadScore.confidence && <p className="text-[10px] text-muted-foreground">{CONFIDENCE_LABEL[leadScore.confidence]}</p>}
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Closing Probability</p>
            <ClosingProbabilityDisplay closing={closing} delta={closingDelta} />
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Temperature</p>
            <Badge className={TEMP_TONE[snapshot.temperature] || ""} data-testid="ai-temperature-value">{snapshot.temperature || "Unknown"}</Badge>
          </div>
        </div>

        <Accordion type="multiple" defaultValue={["background", "strategy"]} className="w-full">
          {narrative.background && (
            <AccordionItem value="background">
              <AccordionTrigger className="text-xs py-2">Background</AccordionTrigger>
              <AccordionContent className="text-sm whitespace-pre-wrap">{narrative.background}</AccordionContent>
            </AccordionItem>
          )}
          {narrative.primary_interest && (
            <AccordionItem value="interest">
              <AccordionTrigger className="text-xs py-2">Primary Interest</AccordionTrigger>
              <AccordionContent className="text-sm whitespace-pre-wrap">{narrative.primary_interest}</AccordionContent>
            </AccordionItem>
          )}
          {narrative.motivations && (
            <AccordionItem value="motivations">
              <AccordionTrigger className="text-xs py-2">Motivations / Decision Drivers</AccordionTrigger>
              <AccordionContent className="text-sm whitespace-pre-wrap">
                {narrative.motivations}
                {narrative.weaknesses && <p className="mt-1 text-muted-foreground">Barriers: {narrative.weaknesses}</p>}
              </AccordionContent>
            </AccordionItem>
          )}
          {snapshot.buying_signals?.length > 0 && (
            <AccordionItem value="signals">
              <AccordionTrigger className="text-xs py-2">Buying Signals ({snapshot.buying_signals.length})</AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-1">
                  {snapshot.buying_signals.map((s, i) => (
                    <li key={i} className="text-sm flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[9px] capitalize">{s.strength.replace("_", " ")}</Badge>
                      {s.signal}
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          )}
          {(genuineObjections.length > 0 || softObjections.length > 0) && (
            <AccordionItem value="objections">
              <AccordionTrigger className="text-xs py-2">Main Objections ({genuineObjections.length + softObjections.length})</AccordionTrigger>
              <AccordionContent className="space-y-2">
                {genuineObjections.length > 0 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Genuine Concerns</p>
                    {genuineObjections.map((o, i) => (
                      <div key={i} className={`rounded border px-2 py-1 mb-1 text-sm ${OBJECTION_TONE[o.severity] || ""}`}>
                        <div className="flex items-start justify-between gap-2">
                          <span>{o.text} <Badge variant="outline" className="text-[9px] ml-1 capitalize">{o.severity}</Badge> <Badge variant="outline" className="text-[9px] capitalize">{o.status.replaceAll("_", " ")}</Badge></span>
                          <CorrectionFlag snapshotId={snapshot.snapshot_id} fieldPath={`objections[genuine:${i}].nature`} aiValue="genuine" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {softObjections.length > 0 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Soft / Deflective Objections</p>
                    {softObjections.map((o, i) => (
                      <div key={i} className="rounded border px-2 py-1 mb-1 text-sm text-muted-foreground">
                        <div className="flex items-start justify-between gap-2">
                          <span>{o.text}</span>
                          <CorrectionFlag snapshotId={snapshot.snapshot_id} fieldPath={`objections[soft:${i}].nature`} aiValue="soft_deflective" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          )}
          {snapshot.unresolved_questions?.length > 0 && (
            <AccordionItem value="questions">
              <AccordionTrigger className="text-xs py-2">Unresolved Questions ({snapshot.unresolved_questions.length})</AccordionTrigger>
              <AccordionContent>
                <ul className="text-sm list-disc pl-4 space-y-0.5">
                  {snapshot.unresolved_questions.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              </AccordionContent>
            </AccordionItem>
          )}
          {snapshot.strategy && (
            <AccordionItem value="strategy">
              <AccordionTrigger className="text-xs py-2">Recommended Strategy</AccordionTrigger>
              <AccordionContent className="text-sm space-y-1.5">
                <p><span className="text-muted-foreground">Primary barrier:</span> {snapshot.strategy.primary_barrier}</p>
                <p><span className="text-muted-foreground">Objective:</span> {snapshot.strategy.recommended_objective}</p>
                <p><span className="text-muted-foreground">Approach:</span> {snapshot.strategy.recommended_approach}</p>
                <p><span className="text-muted-foreground">Useful info:</span> {snapshot.strategy.useful_information}</p>
                <p><span className="text-muted-foreground">Avoid:</span> {snapshot.strategy.avoid}</p>
                <p><span className="text-muted-foreground">Next CTA:</span> {snapshot.strategy.recommended_cta}</p>
                <p><span className="text-muted-foreground">Timing:</span> {snapshot.strategy.followup_timing}{snapshot.strategy.recommended_channel ? ` · ${snapshot.strategy.recommended_channel}` : ""}</p>
              </AccordionContent>
            </AccordionItem>
          )}
          {snapshot.risks?.length > 0 && (
            <AccordionItem value="risks">
              <AccordionTrigger className="text-xs py-2">Risks ({snapshot.risks.length})</AccordionTrigger>
              <AccordionContent className="space-y-1">
                {snapshot.risks.map((r, i) => (
                  <div key={i} className={`rounded border px-2 py-1 text-sm ${OBJECTION_TONE[r.severity] || ""}`}>
                    <Badge variant="outline" className="text-[9px] capitalize mr-1">{r.category.replaceAll("_", " ")}</Badge>
                    {r.description}
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        <div className="flex items-center justify-between pt-1">
          <p className="text-[10px] text-muted-foreground">
            Generated {fmtIST(snapshot.generated_at)} · {snapshot.model || "—"} · AI can be wrong — verify against records
          </p>
          <div className="flex gap-2">
            <Button data-testid="ai-history-button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setHistoryOpen(true)}>
              <HistoryIcon className="h-3.5 w-3.5 mr-1" />History
            </Button>
            {hasCap("ai.customer.generate") && (
              <Button data-testid="ai-intelligence-generate" size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={generate}>
                {busy ? "Generating…" : "Regenerate"}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
      <HistoryDrawer open={historyOpen} setOpen={setHistoryOpen} history={history} />
    </Card>
  );
};
